import {Injectable} from '@angular/core';
import {MessageErrorDetails} from '../../messages/entities/message-error-details.model';
import {Validator} from '@shared/utilities/validator';
import {NewFileViewModel} from '@shared/models/new-file.view-model';
import {RemovableFileViewModel} from '@shared/models/removable-file.view-model';
import {FileValidationError} from '@shared/errors/file-validation-error';

@Injectable({
  providedIn: 'root'
})
export class FileValidationService {

  private static readonly UPLOAD_MINIMUM_SIZE = 1;
  private static readonly UPLOAD_MAXIMUM_SIZE = 10485760; // 10 MiB
  private static readonly UPLOAD_FILENAME_MAXIMUM_LENGTH = 255;
  private static readonly UPLOAD_FILENAME_CHARACTER_BLACKLIST_REGEX = /["*/:<>?\\|]/;
  public static readonly UPLOAD_EXTENSION_WHITELIST = ['.txt', '.pdf', '.jpg', '.jpeg', '.png', '.docx', '.xlsx', '.pptx'];
  private static readonly FILES_MAXIMUM_COUNT = 9;
  private static readonly FILES_MAXIMUM_SIZE = 10485760; // 10 MiB    // TOFIX, change back to this once Typescript bug has been fixed (https://github.com/microsoft/TypeScript/issues/52004) :  FileValidationService.UPLOAD_MAXIMUM_SIZE;

  private validateFile(file: File): void {
    if (!file) {
      throw new FileValidationError('Invalid file', this.genericError());
    }

    if (Validator.isNullOrWhitespace(file.name)) {
      throw new FileValidationError('File must have valid filename', this.genericError());
    }

    if (file.name.length !== file.name.trim().length) {
      throw new FileValidationError('Filename must not have leading or trailing whitespace', this.genericError());
    }

    if (file.name.length > FileValidationService.UPLOAD_FILENAME_MAXIMUM_LENGTH) {
      throw new FileValidationError('Filename must not exceed 255 characters', this.filenameTooLongError());
    }

    const extension = file.name.slice(Math.max(file.name.lastIndexOf('.'), 0) || Infinity);
    if (!FileValidationService.UPLOAD_EXTENSION_WHITELIST.includes(extension.toLowerCase())) {
      throw new FileValidationError('Filename must have an approved extension', this.filenameExtensionUnsupportedError());
    }

    const baseName = file.name.slice(0, (file.name.length - extension.length));
    const secondExtension = baseName.slice(Math.max(baseName.lastIndexOf('.'), 0) || Infinity);
    if (secondExtension.length > 0 && secondExtension.length <= 5) {
      throw new FileValidationError('Filename must have a single extension', this.genericError());
    }

    if (FileValidationService.UPLOAD_FILENAME_CHARACTER_BLACKLIST_REGEX.test(file.name)) {
      throw new FileValidationError('Filename must not contain invalid characters', this.genericError());
    }

    if (file.size < FileValidationService.UPLOAD_MINIMUM_SIZE) {
      throw new FileValidationError('File size must not be too small', this.fileTooSmallError());
    }

    if (file.size > FileValidationService.UPLOAD_MAXIMUM_SIZE) {
      throw new FileValidationError('File size must not be too large', this.fileTooBigError());
    }
  }

  public validateFiles(existingFiles: RemovableFileViewModel[], newFiles: NewFileViewModel[], files: File[]): void {
    for (const file of files) {
      this.validateFile(file);
    }

    const existingFilesCount = existingFiles.reduce((count, existingFile) => count + (existingFile.removed ? 0 : 1), 0);
    if (existingFilesCount + newFiles.length + files.length > FileValidationService.FILES_MAXIMUM_COUNT) {
      throw new FileValidationError('Combined number of existing and new files must not be too many', this.filesTooManyError());
    }

    const newFilesSize = newFiles.reduce((accumulatedSize, newFile) => accumulatedSize + newFile.size, 0);
    const additionalFilesSize = files.reduce((accumulatedSize, additionalFile) => accumulatedSize + additionalFile.size, 0);
    if (newFilesSize + additionalFilesSize > FileValidationService.FILES_MAXIMUM_SIZE) {
      throw new FileValidationError('Combined size of new files must not be too large', this.filesTooBigError());
    }
  }

  public genericError(): MessageErrorDetails {
    return {
      errorMessage: 'Unsupported file.',
      errorDescription: 'Please select a different file.'
    };
  }

  private filenameTooLongError(): MessageErrorDetails {
    return {
      errorMessage: 'Filename exceeds maximum length.',
      errorDescription: 'Please ensure that the filename is less than 256 characters long.'
    };
  }

  private filenameExtensionUnsupportedError(): MessageErrorDetails {
    return {
      errorMessage: 'Unsupported file type.',
      errorDescription: 'Please ensure that the file is an accepted type.'
    };
  }

  private fileTooSmallError(): MessageErrorDetails {
    return {
      errorMessage: 'File is empty.',
      errorDescription: 'Please ensure that the file has content.'
    };
  }

  private fileTooBigError(): MessageErrorDetails {
    return {
      errorMessage: 'File exceeds maximum size.',
      errorDescription: 'Please ensure that the file is less than 10MB.'
    };
  }

  private filesTooManyError(): MessageErrorDetails {
    return {
      errorMessage: 'Files exceed maximum number.',
      errorDescription: 'Please select up to 9 files.'
    };
  }

  private filesTooBigError(): MessageErrorDetails {
    return {
      errorMessage: 'Files exceed maximum combined size.',
      errorDescription: 'Please ensure that the total size of all files is less than 10MB.'
    };
  }
}
